import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError, throwError, switchMap, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Address } from '@shared';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  password_confirmation: string;
  customer_type: string;
}

export interface LoginResponse {
  status?: string; // "alreadyauthenticated" when already logged in
  message: string;
  user?: {
    id: number;
    username: string;
    email?: string;
  };
}

export interface RegisterResponse {
  id: number;
  username: string;
  email: string;
  customer_type: string;
  created_at: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiBaseUrl;

  // Signals for auth state
  public isAuthenticated = signal(false);
  public currentUser = signal<{ id: number; username: string; email?: string } | null>(null);

  constructor() {
    // Check if user is already authenticated on service init
    this.checkAuthStatus();
  }

  /**
   * Check if user has a valid session with backend
   */
  private checkAuthStatus(): void {
    const bookingMode = sessionStorage.getItem('bookingMode');
    
    if (bookingMode === 'authenticated') {
      // Validate with backend
      this.checkUserLoggedIn().subscribe({
        next: (response) => {
          if (response.logged_in && response.user) {
            this.isAuthenticated.set(true);
            this.currentUser.set(response.user);
            
            // Store/update user info in session storage
            sessionStorage.setItem('userId', response.user.id.toString());
            sessionStorage.setItem('userName', response.user.username);
            if (response.user.email) {
              sessionStorage.setItem('userEmail', response.user.email);
            }
          } else {
            this.logout();
          }
        },
        error: () => {
          this.logout();
        }
      });
    }
  }

  /**
   * Check if user is logged in (Backend validation)
   * Endpoint: GET /api/cstm/userloggedin
   * Backend response (SUCCESS): { message: '...', user: { id, username } }
   * Backend response (ERROR): { error: { message: '...' } } with 401 status
   */
  checkUserLoggedIn(): Observable<{ logged_in: boolean; user?: { id: number; username: string; email: string } }> {
    return this.http.get<{ message?: string; user?: { id: number; username: string; email?: string }; error?: any }>(
      `${this.apiUrl}cstm/userloggedin`,
      { withCredentials: true }
    ).pipe(
      switchMap(response => {
        // Backend sends user directly in response (no "data" wrapper for this endpoint)
        if (response.user && response.user.id) {
          return of({
            logged_in: true,
            user: {
              id: response.user.id,
              username: response.user.username,
              email: response.user.email || ''
            }
          });
        }
        // No user in response → not logged in
        return of({ logged_in: false });
      }),
      catchError(error => {
        console.error('User validation error:', error);
        return of({ logged_in: false });
      })
    );
  }

  /**
   * Login with username and password
   * CSRF token is automatically handled by CsrfInterceptor
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}cstm/login`, credentials, {
      withCredentials: true
    }).pipe(
      tap(response => {
        this.storeLoginData(response);
      }),
      catchError(error => {
        console.error('Login error:', error);
        
        // Handle specific error messages from backend
        let userFriendlyMessage = 'Anmeldung fehlgeschlagen';
        
        if (error.status === 401) {
          userFriendlyMessage = 'Benutzername oder Passwort ist falsch. Bitte überprüfen Sie Ihre Eingaben.';
        } else if (error.error?.message) {
          const backendMessage = error.error.message;
          
          if (backendMessage.includes('Invalid username or password') || 
              backendMessage.includes('Invalid credentials')) {
            userFriendlyMessage = 'Benutzername oder Passwort ist falsch. Bitte überprüfen Sie Ihre Eingaben.';
          } else if (backendMessage.includes('Unauthorized')) {
            userFriendlyMessage = 'Zugriff verweigert. Bitte überprüfen Sie Ihre Anmeldedaten.';
          } else if (backendMessage.includes('Too many')) {
            userFriendlyMessage = 'Zu viele Anmeldeversuche. Bitte versuchen Sie es später erneut.';
          } else {
            userFriendlyMessage = backendMessage;
          }
        }
        
        return throwError(() => new Error(userFriendlyMessage));
      })
    );
  }

  /**
   * Register new user
   * CSRF token is automatically handled by CsrfInterceptor
   */
  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}cstm/register`, data, {
      withCredentials: true
    }).pipe(
      tap(response => {
        // If API doesn't return email, use the one from the request
        if (!response.email && data.email) {
          response.email = data.email;
        }
        this.storeRegisterData(response);
      }),
      catchError(error => {
        
        // Handle specific error messages from backend
        let userFriendlyMessage = 'Registrierung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.';
        
        // Check HTTP status
        if (error.status === 422) {
          // Validation error
          if (error.error?.errors) {
            const errors = error.error.errors;
            const errorMessages: string[] = [];
            
            // Check each field
            if (errors.email) {
              errorMessages.push('Diese E-Mail-Adresse ist bereits registriert. Bitte verwenden Sie eine andere E-Mail-Adresse oder melden Sie sich an.');
            }
            if (errors.username) {
              errorMessages.push('Dieser Benutzername ist bereits vergeben. Bitte wählen Sie einen anderen Benutzernamen.');
            }
            if (errors.password) {
              errorMessages.push('Das Passwort erfüllt nicht die Sicherheitsanforderungen. Bitte verwenden Sie ein stärkeres Passwort mit mindestens 10 Zeichen, Groß- und Kleinbuchstaben sowie Zahlen.');
            }
            
            if (errorMessages.length > 0) {
              userFriendlyMessage = errorMessages.join(' ');
            }
          } else if (error.error?.message) {
            const backendMessage = error.error.message;
            
            // Map technical error codes to user-friendly German messages
            if (backendMessage === 'email_already_exists' || 
                backendMessage.includes('email has already been taken')) {
              userFriendlyMessage = 'Diese E-Mail-Adresse ist bereits registriert. Bitte verwenden Sie eine andere E-Mail-Adresse oder melden Sie sich an.';
            } else if (backendMessage === 'username_already_exists' || 
                       backendMessage.includes('username has already been taken')) {
              userFriendlyMessage = 'Dieser Benutzername ist bereits vergeben. Bitte wählen Sie einen anderen Benutzernamen.';
            } else if (backendMessage.includes('password')) {
              userFriendlyMessage = 'Das Passwort erfüllt nicht die Sicherheitsanforderungen. Bitte verwenden Sie ein stärkeres Passwort.';
            } else {
              userFriendlyMessage = backendMessage;
            }
          }
        } else if (error.status === 429) {
          userFriendlyMessage = 'Zu viele Registrierungsversuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.';
        } else if (error.status === 500) {
          userFriendlyMessage = 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
        }
        
        return throwError(() => new Error(userFriendlyMessage));
      })
    );
  }

  /**
   * Request password reset email
   * TODO: Backend-Route muss noch implementiert werden: POST /cstm/forgot-password
   */
  requestPasswordReset(email: string): Observable<{ message: string }> {
    // TODO: Uncomment when backend route is available
    // return this.http.post<{ message: string }>(`${this.apiUrl}cstm/forgot-password`, { email }).pipe(
    //   catchError(error => {
    //     console.error('Password reset request error:', error);
    //     return throwError(() => new Error(error.error?.message || 'Fehler beim Senden der E-Mail'));
    //   })
    // );
    
    // Mock implementation until backend is ready
    return new Observable(observer => {
      setTimeout(() => {
        observer.next({ message: 'Reset email sent' });
        observer.complete();
      }, 1000);
    });
  }

  /**
   * Reset password with token
   * TODO: Backend-Route muss noch implementiert werden: POST /cstm/reset-password
   */
  resetPassword(token: string, password: string): Observable<{ message: string }> {
    // TODO: Uncomment when backend route is available
    // return this.http.post<{ message: string }>(`${this.apiUrl}cstm/reset-password`, { 
    //   token, 
    //   password 
    // }).pipe(
    //   catchError(error => {
    //     console.error('Password reset error:', error);
    //     return throwError(() => new Error(error.error?.message || 'Fehler beim Zurücksetzen des Passworts'));
    //   })
    // );
    
    // Mock implementation until backend is ready
    return new Observable(observer => {
      setTimeout(() => {
        observer.next({ message: 'Password reset successful' });
        observer.complete();
      }, 1000);
    });
  }

  /**
   * Validate reset token
   * TODO: Backend-Route muss noch implementiert werden: POST /cstm/validate-reset-token
   */
  validateResetToken(token: string): Observable<{ valid: boolean }> {
    // TODO: Uncomment when backend route is available
    // return this.http.post<{ valid: boolean }>(`${this.apiUrl}cstm/validate-reset-token`, { token }).pipe(
    //   catchError(error => {
    //     console.error('Token validation error:', error);
    //     return throwError(() => new Error('Token ungültig oder abgelaufen'));
    //   })
    // );
    
    // Mock implementation until backend is ready
    return new Observable(observer => {
      setTimeout(() => {
        observer.next({ valid: true });
        observer.complete();
      }, 500);
    });
  }

  /**
   * Logout user
   */
  logout(): void {
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('bookingMode');
    sessionStorage.removeItem('userEmail');
    sessionStorage.removeItem('userName');
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
  }

  /**
   * Get stored auth token
   */
  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Get auth headers for API requests
   */
  getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    });
  }

  /**
   * Store login authentication data
   */
  private storeLoginData(response: LoginResponse): void {
    // Note: Laravel Sanctum uses session-based auth, no token in response
    sessionStorage.setItem('bookingMode', 'authenticated');
    
    // If user data is provided (normal login), store it
    if (response.user) {
      sessionStorage.setItem('userName', response.user.username);
      sessionStorage.setItem('userId', response.user.id.toString());
      if (response.user.email) {
        sessionStorage.setItem('userEmail', response.user.email);
      }
      this.currentUser.set(response.user);
    }
    // If already authenticated, user data is already in session
    
    this.isAuthenticated.set(true);
  }

  /**
   * Store register authentication data
   */
  private storeRegisterData(response: RegisterResponse): void {
    // Note: Laravel Sanctum uses session-based auth, no token in response
    if (!response) {
      throw new Error('Ungültige Server-Antwort: Keine Daten empfangen');
    }
    
    if (!response.username || !response.id) {
      throw new Error('Ungültige Server-Antwort: Unvollständige Benutzerdaten');
    }
    
    sessionStorage.setItem('bookingMode', 'authenticated');
    sessionStorage.setItem('userName', response.username);
    sessionStorage.setItem('userId', response.id.toString());
    
    // Store email if available
    if (response.email) {
      sessionStorage.setItem('userEmail', response.email);
    }
    
    this.isAuthenticated.set(true);
    this.currentUser.set({
      id: response.id,
      username: response.username,
      email: response.email
    });
  }

  /**
   * Check if user is in guest mode
   */
  isGuestMode(): boolean {
    return sessionStorage.getItem('bookingMode') === 'guest';
  }

  /**
   * Get current booking mode
   */
  getBookingMode(): 'authenticated' | 'guest' | null {
    const mode = sessionStorage.getItem('bookingMode');
    return mode as 'authenticated' | 'guest' | null;
  }

  /**
   * Get user email from session storage
   */
  getUserEmail(): string | null {
    return sessionStorage.getItem('userEmail');
  }

  /**
   * Get customer addresses (only for authenticated users)
   */
  getCustomerAddresses(): Observable<Address[]> {
    return this.http.get<Address[]>(`${this.apiUrl}cstm/addresses`, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error loading addresses:', error);
        return throwError(() => new Error('Fehler beim Laden der Adressen'));
      })
    );
  }

  /**
   * Save a new address
   */
  saveAddress(address: Address): Observable<Address> {
    return this.http.post<Address>(`${this.apiUrl}cstm/addresses`, address, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error saving address:', error);
        return throwError(() => new Error('Fehler beim Speichern der Adresse'));
      })
    );
  }

  /**
   * Update an existing address
   */
  updateAddress(address: Address): Observable<Address> {
    return this.http.put<Address>(`${this.apiUrl}cstm/addresses/${address.id}`, address, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error updating address:', error);
        return throwError(() => new Error('Fehler beim Aktualisieren der Adresse'));
      })
    );
  }

  /**
   * Set an address as default
   */
  setDefaultAddress(addressId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}cstm/addresses/${addressId}/set-default`, {}, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error setting default address:', error);
        return throwError(() => new Error('Fehler beim Setzen der Standard-Adresse'));
      })
    );
  }

  /**
   * Delete an address
   */
  deleteAddress(addressId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}cstm/addresses/${addressId}`, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error deleting address:', error);
        return throwError(() => new Error('Fehler beim Löschen der Adresse'));
      })
    );
  }

  /**
   * Track address usage (update last_used_at and usage_count)
   * Called when address is used in a booking
   */
  trackAddressUsage(addressId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}cstm/addresses/${addressId}/track-usage`, {}, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error tracking address usage:', error);
        // Don't fail the booking if tracking fails
        return of(void 0);
      })
    );
  }
}

